"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useKeyboardOverlayBox } from "@/hooks/useKeyboardOverlayBox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SupportFaqTab } from "./SupportFaqTab";
import { SupportChatTab } from "./SupportChatTab";
import { SupportHomeTab } from "./SupportHomeTab";
import { BusinessHoursSheet } from "./BusinessHoursSheet";
import type { SupportConversation, SupportMessage } from "@/types/support";

type SupportTab = "home" | "faq" | "chat";

interface SupportPanelProps {
  authed: boolean;
  userId: string | null;
  displayName: string | null;
  /** Customer's preferred reading language (from customer_profiles). When
   *  null we fall back to the page's [locale] inside SupportChatTab. */
  preferredLanguage: string | null;
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  loading: boolean;
  isMobile: boolean;
  /** Drives the open/close transition. The parent keeps us mounted briefly
   *  after toggling this to false so the closing animation can play. */
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated: (conv: SupportConversation) => void;
  onMessageSent: (msg: SupportMessage) => void;
  loginReturnTo: string;
  /** Force a specific tab open (e.g. when arriving via push notification). */
  initialTab?: SupportTab;
}

export function SupportPanel({
  authed,
  userId,
  displayName,
  preferredLanguage,
  conversation,
  messages,
  loading,
  isMobile,
  isOpen,
  onClose,
  onConversationCreated,
  onMessageSent,
  loginReturnTo,
  initialTab,
}: SupportPanelProps) {
  const t = useTranslations("support.panel");
  const tLauncher = useTranslations("support.launcher");
  const [tab, setTab] = useState<SupportTab>(initialTab ?? "home");
  const [businessHoursOpen, setBusinessHoursOpen] = useState(false);
  // 패널 높이는 useKeyboardOverlayBox 로 — visualViewport 에 더해, vv 가
  // 키보드에 반응하지 않는 Capacitor iOS WKWebView 의 네이티브 키보드 몫까지
  // 보정한다. keyboardOpen 은 탭바 숨김 같은 표현 토글에만 쓴다(네이티브
  // 플러그인 + 웹 vv 축소 양쪽 감지라 PWA 에서도 동작).
  const { height: viewportHeight, safeTop, keyboardOpen } =
    useKeyboardOverlayBox();
  // Derive the sub-sheet's effective visibility from the panel's prop so
  // closing the panel collapses the sheet too — no effect-driven reset
  // needed. Stale `businessHoursOpen=true` after panel close is harmless
  // because the parent unmounts the panel after its closing transition,
  // re-initializing this state to false on the next open.
  const showBusinessHours = isOpen && businessHoursOpen;

  const businessHoursTrigger = (
    <button
      type="button"
      onClick={() => setBusinessHoursOpen(true)}
      className="flex shrink-0 items-center gap-0.5 rounded-md px-2 py-1 text-[12px] font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
    >
      {t("businessHoursLink")}
      <ChevronRight className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <>
      {/* Overlay (mobile only) — fades with the panel. */}
      {isMobile && (
        <button
          type="button"
          aria-label="close overlay"
          onClick={onClose}
          className={cn(
            "fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ease-out",
            isOpen ? "opacity-100" : "opacity-0",
          )}
        />
      )}
      <div
        role="dialog"
        aria-label={t("title")}
        className={cn(
          // `fixed` itself is a positioned ancestor, so the
          // BusinessHoursSheet's `absolute inset-0` already anchors against
          // this panel — no extra `relative` needed (and it would conflict
          // with `fixed` anyway).
          "fixed z-50 flex flex-col overflow-hidden bg-white",
          isMobile
            ? cn(
                // 높이는 아래 inline style(visualViewport)로만 잡는다. `bottom-0`을
                // 같이 걸면 top+bottom+height가 over-constrained 되어(특히
                // Capacitor KeyboardResize.Body의 iOS WKWebView에서) 명시 height가
                // 무시되고 패널이 전체 높이로 남아 컴포저가 키보드 뒤로 숨는다.
                // 그래서 `top-0`만 고정 — 디바이스 검증된 SearchBar/ChatRoom과 동일.
                "inset-x-0 top-0 w-full shadow-2xl transition-transform duration-420 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform",
                isOpen ? "translate-y-0" : "translate-y-full",
              )
            : cn(
                "bottom-24 right-6 h-160 max-h-[85vh] w-100 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)] transition-[opacity,transform] duration-200 ease-out origin-bottom-right",
                isOpen
                  ? "opacity-100 translate-y-0 scale-100"
                  : "opacity-0 translate-y-2 scale-95",
              ),
        )}
        style={
          isMobile
            ? {
                height:
                  viewportHeight > 0 ? `${viewportHeight}px` : "100dvh",
                // 키보드 등장에 맞춰 높이를 한 번에 부드럽게 줄인다 — vv 이벤트가
                // 계단식으로 와도 transition 이 뭉갠다. transform 은 기존 시트
                // open/close 커브 유지 (inline transition 이 클래스 longhand 를
                // 덮으므로 둘 다 명시).
                transition:
                  "transform 420ms cubic-bezier(0.32,0.72,0,1), height 250ms cubic-bezier(0.38,0.7,0.125,1)",
              }
            : undefined
        }
      >
        {isMobile ? (
          <div
            className="bg-white pt-[max(0.5rem,var(--loc-safe-area-inset-top))]"
            // 키보드 열림 중 env(safe-area-inset-top) 붕괴 대비 동결값
            style={
              safeTop !== null
                ? { paddingTop: `${Math.max(8, safeTop)}px` }
                : undefined
            }
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <Image
                src="/locarora.svg"
                alt="LOCARORA"
                width={120}
                height={28}
                priority
                className="h-6 w-auto shrink-0"
                // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
                unoptimized
              />
              <div className="flex items-center gap-1">
                {businessHoursTrigger}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={tLauncher("close")}
                  className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 active:bg-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <header className="flex items-center justify-between gap-3 bg-white px-4 py-4">
            <Image
              src="/locarora.svg"
              alt="LOCARORA"
              width={120}
              height={28}
              priority
              className="h-7 w-auto shrink-0"
              // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
              unoptimized
            />
            {businessHoursTrigger}
          </header>
        )}

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as SupportTab)}
          className="flex flex-1 flex-col overflow-hidden gap-0"
        >
          <TabsContent
            value="home"
            className="flex flex-1 flex-col overflow-hidden mt-0"
          >
            <SupportHomeTab
              displayName={displayName}
              onStartChat={() => setTab("chat")}
            />
          </TabsContent>
          <TabsContent
            value="faq"
            className="flex flex-1 flex-col overflow-hidden mt-0"
          >
            <SupportFaqTab />
          </TabsContent>
          <TabsContent
            value="chat"
            className="flex flex-1 flex-col overflow-hidden mt-0"
          >
            <SupportChatTab
              authed={authed}
              userId={userId}
              keyboardOpen={keyboardOpen}
              preferredLanguage={preferredLanguage}
              conversation={conversation}
              messages={messages}
              loading={loading}
              onConversationCreated={onConversationCreated}
              onMessageSent={onMessageSent}
              loginReturnTo={loginReturnTo}
            />
          </TabsContent>

          {/* Bottom tab bar — Channel-Talk style, lives at the bottom of the
              panel so the chat composer sits directly above it. Mirror the
              BottomNavBar pattern: outer wrapper holds `pb-safe` so iOS PWA's
              home-indicator inset doesn't eat into the 56px content row.

              채팅 탭에서 키보드가 열리면 탭바를 숨긴다 — 그래야 입력창이 키보드
              바로 위에 붙어 전체화면/파트너 채팅과 동일한 느낌이 된다. 탭바가
              입력창과 키보드 사이에 끼면 입력창이 키보드에서 멀어져 어색해진다.
              홈/FAQ 탭에서는 키보드가 안 뜨므로 탭바는 그대로 유지된다. */}
          <div
            className={cn(
              // 탭바는 애니메이션 없이 즉시 숨긴다 — max-height transition 이
              // 패널 높이 축소와 겹치면 컴포저가 출렁인다.
              "shrink-0 bg-white",
              !keyboardOpen && "pb-safe",
              tab === "chat" && keyboardOpen && "hidden",
            )}
          >
          <TabsList className="grid h-14 w-full grid-cols-3 rounded-none bg-white p-0">
            <TabsTrigger
              value="home"
              className="flex h-full flex-col items-center justify-center gap-0.5 rounded-none text-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-orange-500 data-[state=inactive]:text-gray-500"
            >
              <HomeIcon active={tab === "home"} />
              {t("tabs.home")}
            </TabsTrigger>
            <TabsTrigger
              value="chat"
              className="flex h-full flex-col items-center justify-center gap-0.5 rounded-none text-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-orange-500 data-[state=inactive]:text-gray-500"
            >
              <ChatIcon active={tab === "chat"} />
              {t("tabs.chat")}
            </TabsTrigger>
            <TabsTrigger
              value="faq"
              className="flex h-full flex-col items-center justify-center gap-0.5 rounded-none text-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-orange-500 data-[state=inactive]:text-gray-500"
            >
              <FaqIcon active={tab === "faq"} />
              {t("tabs.faq")}
            </TabsTrigger>
          </TabsList>
          </div>
        </Tabs>

        {/* BusinessHoursSheet is a panel-direct child so its overlay covers
            the header as well, not just the tab area. */}
        <BusinessHoursSheet
          open={showBusinessHours}
          onClose={() => setBusinessHoursOpen(false)}
        />
      </div>
    </>
  );
}

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z" />
    </svg>
  );
}

function FaqIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.4 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" />
    </svg>
  );
}

function ChatIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill={active ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a8 8 0 0 1-11.5 7.2L4 21l1.7-5A8 8 0 1 1 21 12z" />
    </svg>
  );
}
